import React from "react";
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Image,
  TouchableOpacity,
  Linking,
} from "react-native";
import { router } from "expo-router";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import i18n from "../../locales/i18n";
import WebSidebar from "./WebSidebar";

// Import settings image for header
import SettingsImage from "../../assets/images/settings.png";

const WebContactScreen: React.FC = () => {
  const { locale } = useSelector((state: RootState) => state.app);

  // Check if current language is RTL (Arabic)
  const isRTL = locale === "ar" || i18n.locale === "ar";

  const handleGoBack = () => {
    router.push("/(drawer)/params" as any);
  };

  const contactItems = [
    {
      id: "email",
      label: "khdame.contact@gmail.com",
      icon: "✉️",
      color: "#FBC369",
      action: () => Linking.openURL("mailto:khdame.contact@gmail.com"),
    },
    {
      id: "phone",
      label: "05 40 84 93 66",
      icon: "📞",
      color: "#179717",
      action: () => Linking.openURL("tel:0540849366"),
    },
    {
      id: "facebook",
      label: "Khdameخدام/",
      icon: "f",
      isFacebookIcon: true,
      color: "#039BE5",
      action: () =>
        Linking.openURL(
          "https://www.facebook.com/profile.php?id=61575224393561#"
        ),
    },
    {
      id: "instagram",
      label: "khdame_app",
      icon: "📷",
      isInstagramIcon: true,
      color: "#FF6342",
      action: () => Linking.openURL("https://www.instagram.com/khdame_app"),
    },
    {
      id: "website",
      label: "khdame.com",
      icon: "🌐",
      color: "#E9E9E9",
      textColor: "#333",
      action: () => Linking.openURL("https://khdame.com"),
    },
  ];

  return (
    <div
      key={`contact-screen-${locale}`}
      className="flex flex-1 bg-[#F5F5F5] min-h-screen flex-row"
    >
      <WebSidebar />

      <View style={styles.mainContent}>
        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
        >
          {/* Header with breadcrumb and blue bar */}
          <View style={styles.headerSection}>
            <div
              className={`flex flex-row items-center gap-4 ${isRTL ? "justify-end flex-row-reverse" : "justify-start"}`}
            >
              <View style={styles.headerIconContainer}>
                <Image
                  source={SettingsImage}
                  style={styles.headerIcon}
                  resizeMode="contain"
                />
              </View>
              <View style={styles.breadcrumb}>
                <TouchableOpacity onPress={handleGoBack}>
                  <Text style={styles.breadcrumbLink}>
                    {i18n.t("settings")}
                  </Text>
                </TouchableOpacity>
                <Text style={styles.breadcrumbSeparator}>{"  >  "}</Text>
                <Text style={styles.breadcrumbCurrent}>
                  {i18n.t("contactus")}
                </Text>
              </View>
            </div>
            <View style={styles.headerBar} />
          </View>

          {/* Contact Items */}
          <View style={styles.contactContainer}>
            {contactItems.map((item) => (
              <TouchableOpacity
                key={item.id}
                style={[styles.contactItem, { backgroundColor: item.color }]}
                onPress={item.action}
              >
                {item.isFacebookIcon ? (
                  <View style={styles.facebookIconContainer}>
                    <Text style={styles.facebookIcon}>f</Text>
                  </View>
                ) : item.isInstagramIcon ? (
                  <View style={styles.instagramIconContainer}>
                    <Text style={styles.instagramIcon}>📷</Text>
                  </View>
                ) : (
                  <View style={styles.iconContainer}>
                    <Text style={styles.contactIcon}>{item.icon}</Text>
                  </View>
                )}
                <Text
                  style={[
                    styles.contactLabel,
                    item.textColor ? { color: item.textColor } : {},
                  ]}
                >
                  {item.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>
      </View>
    </div>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F5F5F5",
    minHeight: "100vh" as any,
  },
  containerRTL: {
    flexDirection: "row-reverse",
  } as any,
  mainContent: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
    padding: 20,
  },
  headerSection: {
    marginBottom: 40,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 16,
    paddingVertical: 16,
    paddingHorizontal: 24,
  },
  breadcrumb: {
    flexDirection: "row",
    alignItems: "center",
  },
  breadcrumbLink: {
    fontSize: 28,
    color: "#333",
  },
  breadcrumbSeparator: {
    fontSize: 28,
    color: "#999",
    marginHorizontal: 4,
  },
  breadcrumbCurrent: {
    fontSize: 32,
    fontWeight: "bold",
    color: "#333",
  },
  headerIconContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: "#E8F4F8",
    borderWidth: 4,
    borderColor: "#1379A2",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  headerIcon: {
    width: 70,
    height: 70,
  },
  headerBar: {
    height: 8,
    backgroundColor: "#1379A2",
    borderRadius: 4,
    marginTop: 8,
  },
  contactContainer: {
    alignItems: "center",
    gap: 16,
    paddingHorizontal: 60,
  },
  contactItem: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 30,
    paddingVertical: 16,
    paddingHorizontal: 24,
    width: "100%",
    maxWidth: 600,
  },
  iconContainer: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: "rgba(255, 255, 255, 0.3)",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 16,
  },
  contactIcon: {
    fontSize: 28,
  },
  facebookIconContainer: {
    width: 50,
    height: 50,
    borderRadius: 12,
    backgroundColor: "#FFFFFF",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 16,
  },
  facebookIcon: {
    fontSize: 32,
    fontWeight: "bold",
    color: "#039BE5",
  },
  instagramIconContainer: {
    width: 50,
    height: 50,
    borderRadius: 12,
    backgroundColor: "#FFFFFF",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 16,
  },
  instagramIcon: {
    fontSize: 28,
  },
  contactLabel: {
    flex: 1,
    fontSize: 20,
    color: "#FFFFFF",
    fontWeight: "500",
    textAlign: "center",
  },
});

export default WebContactScreen;
